/**
 * Keep focused inputs above the soft keyboard on Capacitor Android/iOS.
 * Edge-to-edge (decorFitsSystemWindows=false) breaks adjustResize alone.
 */
import { Capacitor } from '@capacitor/core';

const KEYBOARD_VAR = '--keyboard-height';

function setKeyboardHeight(px: number) {
  document.documentElement.style.setProperty(KEYBOARD_VAR, `${Math.max(0, Math.round(px))}px`);
}

function scrollFocusedIntoView() {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && !el.isContentEditable) return;

  const scroller =
    el.closest('.app-scroll') ||
    el.closest('[data-keyboard-scroll]') ||
    document.scrollingElement ||
    document.documentElement;

  window.requestAnimationFrame(() => {
    try {
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
    } catch {
      el.scrollIntoView(true);
    }
    if (scroller instanceof HTMLElement) {
      const rect = el.getBoundingClientRect();
      const kb = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(KEYBOARD_VAR)) || 0;
      const safeBottom = window.innerHeight - kb - 24;
      if (rect.bottom > safeBottom) {
        scroller.scrollTop += rect.bottom - safeBottom;
      }
    }
  });
}

export async function initNativeKeyboard(): Promise<void> {
  if (typeof window === 'undefined' || !Capacitor.isNativePlatform()) return;

  document.documentElement.style.setProperty(KEYBOARD_VAR, '0px');

  try {
    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
    await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
    await Keyboard.setScroll({ isDisabled: false });

    await Keyboard.addListener('keyboardWillShow', (info) => {
      setKeyboardHeight(info.keyboardHeight || 0);
      scrollFocusedIntoView();
    });
    await Keyboard.addListener('keyboardDidShow', (info) => {
      setKeyboardHeight(info.keyboardHeight || 0);
      scrollFocusedIntoView();
    });
    await Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardHeight(0);
    });
    await Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    });
  } catch {
    /* plugin optional in web builds */
  }

  document.addEventListener(
    'focusin',
    () => {
      window.setTimeout(scrollFocusedIntoView, 80);
    },
    true
  );
}
